import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import FavoriteIcon from "@mui/icons-material/Favorite";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import { formatPlayerStats } from "../../utils/liveTrainingPlayerContext";
import {
  getZoneThresholds,
  ZONE_DEFS,
} from "../../features/trainingSummary/zoneUtils";
import {
  computeTrend,
  isSignalStale,
  latestBpmFromSamples,
  MIN_SAMPLES_FOR_CHART,
} from "../../utils/liveHrHistoryBuffer";
import { LiveHrTrendChart } from "./LiveHrTrendChart";
import { ComplianceNotice } from "../compliance/ComplianceNotice";

function zoneForBpm(bpm, mhr) {
  if (!bpm || bpm <= 0) return null;
  const thresholds = getZoneThresholds(mhr);
  for (let z = 5; z >= 0; z -= 1) {
    if (bpm >= thresholds[z][0]) return z;
  }
  return 0;
}

function zoneShortName(zone) {
  const def = ZONE_DEFS.find((d) => d.zone === zone);
  if (!def) return "";
  const parts = def.name.split("(");
  return parts[0].trim();
}

function TrendIndicator({ trend, t }) {
  if (trend === "rising") {
    return (
      <span className="lt-hr-trend-panel__trend lt-hr-trend-panel__trend--rising">
        <TrendingUpIcon fontSize="inherit" />
        {t("liveTrainingHrTrendRising") || "Rising"}
      </span>
    );
  }
  if (trend === "easing") {
    return (
      <span className="lt-hr-trend-panel__trend lt-hr-trend-panel__trend--easing">
        <TrendingDownIcon fontSize="inherit" />
        {t("liveTrainingHrTrendEasing") || "Easing"}
      </span>
    );
  }
  if (trend === "steady") {
    return (
      <span className="lt-hr-trend-panel__trend lt-hr-trend-panel__trend--steady">
        <TrendingFlatIcon fontSize="inherit" />
        {t("liveTrainingHrTrendSteady") || "Steady"}
      </span>
    );
  }
  return null;
}

export function LiveHrTrendPanel({
  player,
  role = null,
  samples = [],
  mhr = 180,
  layout = "panel",
  headerExtra = null,
  toggle = null,
  liveBpm = null,
  chartHeight,
  discreteMeta = null,
}) {
  const { t } = useTranslation();
  const stats = formatPlayerStats(player?.classement, player?.rankingPoints, t);
  const bpm = liveBpm ?? latestBpmFromSamples(samples);
  const trend = useMemo(() => computeTrend(samples), [samples]);
  const stale = isSignalStale(samples);
  const collecting = samples.length < MIN_SAMPLES_FOR_CHART;
  const zone = zoneForBpm(bpm, mhr);
  const zoneLabel = zone != null ? zoneShortName(zone) : null;
  const compact = layout === "compact";
  const arena = layout === "arena";
  const resolvedChartHeight = chartHeight ?? (compact ? 56 : arena ? 160 : 120);

  const metaParts = [player?.name || "—"];
  if (role) metaParts.push(role);
  if (stats) metaParts.push(stats);
  const metaLine = discreteMeta || metaParts.join(" · ");

  const ariaLabel = [
    bpm != null ? `${bpm} BPM` : t("liveTrainingHrTrendNoSignal") || "No signal",
    trend !== "unknown"
      ? t(`liveTrainingHrTrend${trend.charAt(0).toUpperCase()}${trend.slice(1)}`) ||
        trend
      : null,
    zoneLabel ? `Zone ${zone} ${zoneLabel}` : null,
  ]
    .filter(Boolean)
    .join(", ");

  const hrInfo = <ComplianceNotice variant="heartRate" mode="info" />;

  if (arena) {
    return (
      <div className="lt-hr-trend-panel lt-hr-trend-panel--arena">
        <div className="lt-hr-trend-panel__arena-head">
          <p className="lt-hr-trend-panel__arena-meta" title={metaLine}>
            {metaLine}
          </p>
          <div className="lt-hr-trend-panel__arena-tools">
            {bpm != null ? (
              <span className="lt-hr-trend-panel__bpm-chip lt-hr-trend-panel__bpm-chip--arena">
                <FavoriteIcon className="lt-hr-trend-panel__bpm-icon" fontSize="inherit" />
                {bpm}
                {hrInfo}
              </span>
            ) : (
              hrInfo
            )}
            {toggle}
          </div>
        </div>

        <div className="lt-hr-trend-panel__chart-grow">
          <LiveHrTrendChart
            samples={samples}
            mhr={mhr}
            fill
            arena
            stale={stale}
            collecting={collecting}
            ariaLabel={ariaLabel}
          />
        </div>

        <div className="lt-hr-trend-panel__arena-foot">
          <span className="lt-hr-trend-panel__window">
            {t("liveTrainingHrTrendLastMinute") || "Last 60s"}
          </span>
          {collecting ? (
            <span className="lt-hr-trend-panel__status">
              {t("liveTrainingHrTrendCollecting") || "Collecting HR data…"}
            </span>
          ) : stale ? (
            <span className="lt-hr-trend-panel__status lt-hr-trend-panel__status--stale">
              {t("liveTrainingHrTrendNoSignal") || "No recent signal"}
            </span>
          ) : (
            <TrendIndicator trend={trend} t={t} />
          )}
          {zoneLabel ? (
            <span className="lt-hr-trend-panel__arena-zone">
              Z{zone} {zoneLabel}
            </span>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`lt-hr-trend-panel${
        compact ? " lt-hr-trend-panel--compact" : " lt-hr-trend-panel--panel"
      }`}
    >
      <div className="lt-hr-trend-panel__top">
        {bpm != null ? (
          <span className="lt-hr-trend-panel__bpm-chip">
            <FavoriteIcon className="lt-hr-trend-panel__bpm-icon" fontSize="inherit" />
            {bpm}
            {hrInfo}
          </span>
        ) : (
          hrInfo
        )}
        {toggle}
      </div>

      <p className="lt-hr-trend-panel__meta">{metaLine}</p>
      {headerExtra}

      <LiveHrTrendChart
        samples={samples}
        mhr={mhr}
        height={resolvedChartHeight}
        compact={compact}
        stale={stale}
        collecting={collecting}
        ariaLabel={ariaLabel}
      />

      <div className="lt-hr-trend-panel__footer">
        <span className="lt-hr-trend-panel__window">
          {t("liveTrainingHrTrendLastMinute") || "Last 60s"}
        </span>
        {collecting ? (
          <span className="lt-hr-trend-panel__status">
            {t("liveTrainingHrTrendCollecting") || "Collecting HR data…"}
          </span>
        ) : stale ? (
          <span className="lt-hr-trend-panel__status lt-hr-trend-panel__status--stale">
            {t("liveTrainingHrTrendNoSignal") || "No recent signal"}
          </span>
        ) : (
          <TrendIndicator trend={trend} t={t} />
        )}
      </div>

      {zoneLabel && !compact ? (
        <p className="lt-hr-trend-panel__zone">
          {t("zone") || "Zone"} {zone} · {zoneLabel}
        </p>
      ) : null}
    </div>
  );
}

export default LiveHrTrendPanel;
